import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { 
  MultiModelConfig, 
  UploadedFile, 
  MultimodalSession, 
  MultimodalResponse,
  DEFAULT_MULTIMODAL_CONFIG
} from '../types/multimodal';
import { MultiModelSelector } from './MultiModelSelector';
import { FileUpload } from './FileUpload';
import { MultimodalResults } from './MultimodalResults';
import { MultimodalService } from '../services/multimodalService';
import { getRecommendedModelsByCategory } from '../services/modelService';
import { OpenRouterConfig } from './OpenRouterConfig';

interface MultimodalWorkflowProps {
  isOpen: boolean;
  onClose: () => void;
  initialPrompt?: string;
  onApplyResult?: (content: string) => void;
  baseConfig?: OpenRouterConfig;
}

export const MultimodalWorkflow: React.FC<MultimodalWorkflowProps> = ({
  isOpen,
  onClose,
  initialPrompt = '',
  onApplyResult,
  baseConfig
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  
  // Estados principais
  const [currentStep, setCurrentStep] = useState<'setup' | 'processing' | 'results'>('setup');
  const [prompt, setPrompt] = useState('');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [models, setModels] = useState<MultiModelConfig[]>([]);
  const [session, setSession] = useState<MultimodalSession | null>(null);
  const [responses, setResponses] = useState<MultimodalResponse[]>([]);
  
  // Estados de UI
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<Record<string, string>>({});
  const [estimatedTokens, setEstimatedTokens] = useState(0);
  
  const multimodalService = new MultimodalService();

  // Sincronizar prompt inicial quando modal abrir
  useEffect(() => {
    if (isOpen && initialPrompt && initialPrompt !== prompt) {
      setPrompt(initialPrompt);
    }
  }, [isOpen, initialPrompt, prompt]);

  // Inicializar com modelos padrão da API
  useEffect(() => {
    if (models.length === 0 && baseConfig?.apiKey) {
      loadDefaultModels();
    }
  }, [baseConfig]);

  // Load default models from API
  const loadDefaultModels = async () => {
    try {
      const recommendedModels = await getRecommendedModelsByCategory('vision');
      const defaultModels: MultiModelConfig[] = recommendedModels.slice(0, 3).map((modelInfo, index) => ({
        id: `model-${Date.now()}-${index}`,
        name: modelInfo.name,
        config: {
          apiKey: baseConfig?.apiKey || '',
          model: modelInfo.id,
          temperature: 0.7,
          maxTokens: modelInfo.id.includes('reasoning') ? 4000 : 
                     modelInfo.id.includes('o1') ? 4000 : 2000
        },
        enabled: true,
        order: index,
        capabilities: ['text', 'image']
      }));
      setModels(defaultModels);
    } catch (error) {
      console.error('Error loading default models:', error);
      // Fallback to basic model if API fails
      const fallbackModel: MultiModelConfig = {
        id: `model-${Date.now()}`,
        name: 'Claude 3.5 Sonnet',
        config: {
          apiKey: baseConfig?.apiKey || '',
          model: 'anthropic/claude-3.5-sonnet',
          temperature: 0.7,
          maxTokens: 4000
        },
        enabled: true,
        order: 0,
        capabilities: ['text', 'image']
      };
      setModels([fallbackModel]);
    }
  };

  // Atualizar estimativa de tokens
  useEffect(() => {
    if (prompt || files.length > 0) {
      const tokens = multimodalService.estimateTokens(prompt, files);
      setEstimatedTokens(tokens);
    }
  }, [prompt, files]);

  // Processar com múltiplos modelos
  const handleProcess = async () => {
    if (!prompt.trim() && files.length === 0) {
      alert(t('multimodal.promptOrFilesRequired', 'Please provide a prompt or upload files'));
      return;
    }

    const enabledModels = models.filter(m => m.enabled);
    if (enabledModels.length === 0) {
      alert(t('multimodal.noModelsEnabled', 'Please enable at least one model'));
      return;
    }

    setIsProcessing(true);
    setCurrentStep('processing');
    setResponses([]);
    setProcessingStatus({});

    // Criar nova sessão
    const newSession = multimodalService.createSession(prompt, files, models);
    setSession(newSession);

    try {
      const results = await multimodalService.processWithMultipleModels(
        prompt,
        files,
        enabledModels,
        language,
        (modelId, status, response) => {
          setProcessingStatus(prev => ({
            ...prev,
            [modelId]: status
          }));

          if (response) {
            setResponses(prev => {
              const existing = prev.find(r => r.modelId === modelId);
              if (existing) {
                return prev.map(r => r.modelId === modelId ? response : r);
              }
              return [...prev, response];
            });
          }
        }
      );

      // Atualizar sessão com respostas
      const updatedSession = multimodalService.updateSessionWithResponses(newSession, results);
      setSession(updatedSession);
      setCurrentStep('results');
    } catch (error) {
      console.error('Error in multimodal processing:', error);
      alert(t('multimodal.processingError', 'Error processing request. Please try again.'));
      setCurrentStep('setup');
    } finally {
      setIsProcessing(false);
    }
  };

  // Aplicar resposta ao editor principal
  const handleApplyResponse = useCallback((response: MultimodalResponse) => {
    if (onApplyResult) {
      onApplyResult(response.content);
      onClose();
    }
  }, [onApplyResult, onClose]);

  // Exportar sessão
  const handleExportSession = useCallback((session: MultimodalSession) => {
    const exportData = multimodalService.exportSession(session);
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `multimodal-session-${session.id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // Voltar ao setup
  const handleBackToSetup = () => {
    setCurrentStep('setup');
    setResponses([]);
    setProcessingStatus({});
  };

  // Renderizar steps indicator
  const renderStepsIndicator = () => {
    const steps = [
      { id: 'setup', label: t('multimodal.setup', 'Setup'), icon: '⚙️' },
      { id: 'processing', label: t('multimodal.processing', 'Processing'), icon: '🔄' },
      { id: 'results', label: t('multimodal.results', 'Results'), icon: '📊' }
    ];

    return (
      <div className="flex items-center justify-center space-x-4 mb-8">
        {steps.map((step, index) => {
          const isActive = currentStep === step.id;
          const isCompleted = steps.findIndex(s => s.id === currentStep) > index;
          
          return (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
                isActive ? 'bg-purple-500 text-white' :
                isCompleted ? 'bg-green-500 text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {isCompleted ? '✓' : step.icon}
              </div>
              <span className={`ml-2 text-sm font-medium ${
                isActive ? 'text-purple-600' : 'text-gray-500'
              }`}>
                {step.label}
              </span>
              {index < steps.length - 1 && (
                <div className={`ml-4 w-8 h-0.5 ${
                  isCompleted ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Renderizar setup
  const renderSetup = () => (
    <div className="space-y-6">
      {/* Prompt Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('multimodal.promptLabel', 'Your Prompt')}
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t('multimodal.promptPlaceholder', 'Describe what you want the AI models to analyze or help with...')}
          className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
        />
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('multimodal.filesLabel', 'Upload Files (Optional)')}
        </label>
        <FileUpload
          files={files}
          onFilesChange={setFiles}
          maxFiles={DEFAULT_MULTIMODAL_CONFIG.maxFiles}
          maxFileSize={DEFAULT_MULTIMODAL_CONFIG.maxFileSize}
          enabledTypes={DEFAULT_MULTIMODAL_CONFIG.enabledFileTypes}
        />
      </div>

      {/* Models Configuration */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            {t('multimodal.modelsLabel', 'AI Models')} ({models.filter(m => m.enabled).length} {t('multimodal.enabled', 'enabled')})
          </label>
          <button
            onClick={() => setShowModelSelector(true)}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
          >
            {t('multimodal.configureModels', 'Configure Models')}
          </button>
        </div>
        
        {/* Models Preview */}
        {models.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {models.filter(m => m.enabled).map((model, index) => (
              <div key={model.id} className="p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-purple-500 rounded flex items-center justify-center text-white text-xs font-medium">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{model.name}</div>
                    <div className="text-xs text-gray-500 truncate">{model.config.model}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Token Estimation */}
      {estimatedTokens > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-blue-800">
              {t('multimodal.estimatedTokens', 'Estimated tokens')}: <strong>{estimatedTokens.toLocaleString()}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
        >
          {t('multimodal.cancel', 'Cancel')}
        </button>
        <button
          onClick={handleProcess}
          disabled={(!prompt.trim() && files.length === 0) || models.filter(m => m.enabled).length === 0}
          className="px-8 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('multimodal.startAnalysis', 'Start Analysis')}
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">
                  🎯 {t('multimodal.title', 'Multimodal AI Analysis')}
                </h2>
                <p className="text-purple-100 mt-1">
                  {t('multimodal.description', 'Analyze content with multiple AI models simultaneously')}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 pb-8">
            {renderStepsIndicator()}

            {currentStep === 'setup' && renderSetup()}

            {/* Processing Step */}
            {currentStep === 'processing' && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🤖</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {t('multimodal.analyzingContent', 'Analyzing your content...')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('multimodal.multipleModels', 'Multiple AI models are working on your request')}
                </p>
                
                {/* Processing Status */}
                <div className="max-w-md mx-auto space-y-3">
                  {models.filter(m => m.enabled).map((model) => {
                    const status = processingStatus[model.id] || 'pending';
                    return (
                      <div key={model.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className={`w-4 h-4 rounded-full ${
                          status === 'completed' ? 'bg-green-500' :
                          status === 'error' ? 'bg-red-500' :
                          status === 'started' ? 'bg-blue-500 animate-pulse' :
                          'bg-gray-300'
                        }`} />
                        <span className="flex-1 text-left text-sm">{model.name}</span>
                        <span className="text-xs text-gray-500 capitalize">{status}</span>
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={handleBackToSetup}
                  className="mt-8 px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {t('multimodal.backToSetup', '← Back to Setup')}
                </button>
              </div>
            )}

            {/* Results Step */}
            {currentStep === 'results' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {t('multimodal.analysisResults', 'Analysis Results')}
                    </h3>
                    <p className="text-gray-600">
                      {responses.filter(r => !r.error).length} successful responses from {responses.length} models
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleBackToSetup}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      {t('multimodal.newAnalysis', 'New Analysis')}
                    </button>
                    {session && (
                      <button
                        onClick={() => handleExportSession(session)}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        {t('multimodal.exportResults', 'Export Results')}
                      </button>
                    )}
                  </div>
                </div>

                <MultimodalResults
                  session={session}
                  responses={responses}
                  isProcessing={false}
                  onClose={onClose}
                  onApplyResponse={handleApplyResponse}
                  onExportSession={handleExportSession}
                />
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Model Selector Modal */}
      <MultiModelSelector
        isOpen={showModelSelector}
        onClose={() => setShowModelSelector(false)}
        onSave={setModels}
        currentConfigs={models}
        baseConfig={baseConfig}
      />
    </>
  );
};